// build: 2026-10-08 (stale-chunk self-heal)
import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import "./styles/clerk-house.css";
import "./styles/nav-tickets.css";

// Stale tab after a deploy: Vite fires vite:preloadError when a lazy chunk is gone.
// Reload once (guarded) so the visitor never sees the error screen; a second failure
// in the same session falls through to the ErrorBoundary.
window.addEventListener("vite:preloadError", (event) => {
  const KEY = "cep.preload.reloadedAt";
  const now = Date.now();
  let last = 0;
  try { last = Number(sessionStorage.getItem(KEY) || 0); } catch { /* storage blocked */ }
  if (now - last < 30_000) return; // already reloaded for this in the last 30 s: let the ErrorBoundary show
  try { sessionStorage.setItem(KEY, String(now)); } catch { /* storage blocked */ }
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

const AppContent = (
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);

const root = createRoot(document.getElementById("root")!);
root.render(AppContent);

// Initialize standalone Clerk SDK
// Clerk's hosted cards say "to continue to {{applicationName}}". Override the wording here so every
// card reads "California Equity Program" whatever the dashboard name is.
const CLERK_APP_NAME = "California Equity Program";
const CEP_FONT = 'Barlow, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const CLERK_LOAD_OPTIONS = {
  // Light green/white house palette. Card details live in styles/clerk-house.css.
  appearance: {
    layout: {
      logoImageUrl: "/cep-logo.png",
      logoPlacement: "inside",
      socialButtonsVariant: "blockButton",
      socialButtonsPlacement: "top",
      shimmer: false,
    },
    variables: {
      colorPrimary: "#1E4D2B",
      colorBackground: "#FFFFFF",
      colorText: "#1A1A19",
      colorTextSecondary: "#5B6660",
      colorTextOnPrimaryBackground: "#FFFFFF",
      colorInputBackground: "#FFFFFF",
      colorInputText: "#1A1A19",
      colorNeutral: "#5B6660",
      colorDanger: "#B91C1C",
      colorSuccess: "#16A34A",
      colorWarning: "#D946EF",
      borderRadius: "12px",
      fontFamily: CEP_FONT,
      fontFamilyButtons: CEP_FONT,
    },
    elements: {
      formFieldInput: { border: "1px solid #D9E2DC", backgroundColor: "#FFFFFF" },
    },
  },
  localization: {
    signIn: {
      start: { subtitle: `to continue to ${CLERK_APP_NAME}` },
      password: { subtitle: `to continue to ${CLERK_APP_NAME}` },
      emailCode: { subtitle: `to continue to ${CLERK_APP_NAME}` },
      emailLink: { subtitle: `to continue to ${CLERK_APP_NAME}` },
      phoneCode: { subtitle: `to continue to ${CLERK_APP_NAME}` },
      alternativeMethods: { subtitle: `to continue to ${CLERK_APP_NAME}` },
    },
    signUp: {
      start: { subtitle: `to continue to ${CLERK_APP_NAME}` },
      emailCode: { subtitle: `to continue to ${CLERK_APP_NAME}` },
      emailLink: { subtitle: `to continue to ${CLERK_APP_NAME}` },
      continue: { subtitle: `to continue to ${CLERK_APP_NAME}` },
    },
  },
};
if (typeof window !== "undefined") {
  const initClerk = () => {
    const clerk = (window as any).Clerk;
    if (clerk && typeof clerk.load === "function" && !clerk.loaded) {
      clerk.load(CLERK_LOAD_OPTIONS).then(() => {
        console.log("[Clerk] Standalone SDK initialized");
      }).catch((e: unknown) => {
        console.error("[Clerk] Failed to load:", e);
      });
    } else if (!clerk) {
      setTimeout(initClerk, 200);
    }
  };
  initClerk();
}
